import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWHEPStream } from "@/lib/cctv";
import { GridLayout } from "@safers/ui/organisms";
import {
  Widget,
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "@safers/ui/molecules";
import type { GridTemplate, LayoutChangeEvent } from "@safers/ui";
import type { CCTVStreamItem } from "@/hooks/useCCTVStreams";
import { StreamLoadingOverlay, StreamErrorOverlay, StreamStatusBadge } from "@/components/cctv";
import {
  useIsFullscreen,
  useLandscapeInFullscreen,
  useLandscapeVideo,
} from "@/hooks/useIsFullscreen";
import { PTZControl } from "./PTZControl";
import { isPlaybackEnabledSite, type StompEventResponse } from "@/services";

function CCTVSelect({
  items,
  selected,
  onChange,
}: {
  items: CCTVStreamItem[];
  selected: string;
  onChange: (streamName: string) => void;
}) {
  return (
    <Select value={selected} onValueChange={onChange}>
      <SelectTrigger className="w-auto min-w-[12rem] max-w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.streamName} value={item.streamName}>
            {item.displayName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type GridMode = "1x1" | "2x2" | "3x3" | "4x4" | "spot";

const GRID_MODE_LABELS: Record<GridMode, string> = {
  "1x1": "1×1",
  "2x2": "2×2",
  "3x3": "3×3",
  "4x4": "4×4",
  spot: "1+7",
};

const GRID_MODES: GridMode[] = ["1x1", "2x2", "3x3", "4x4", "spot"];

function createSquareTemplate(id: string, name: string, cols: number): GridTemplate {
  const total = cols * cols;
  return {
    id,
    name,
    columns: cols,
    rows: cols,
    cells: Array.from({ length: total }, (_, i) => ({
      id: `c${i + 1}`,
      colStart: (i % cols) + 1,
      colSpan: 1,
      rowStart: Math.floor(i / cols) + 1,
      rowSpan: 1,
    })),
  };
}

const GRID_TEMPLATES: Record<"2x2" | "3x3" | "4x4", GridTemplate> = {
  "2x2": createSquareTemplate("2x2", "2×2", 2),
  "3x3": createSquareTemplate("3x3", "3×3", 3),
  "4x4": createSquareTemplate("4x4", "4×4", 4),
};

const SPOTLIGHT_TEMPLATE: GridTemplate = {
  id: "spot",
  name: "1+7",
  columns: 4,
  rows: 4,
  cells: [
    { id: "big", colStart: 1, colSpan: 3, rowStart: 1, rowSpan: 3 },
    { id: "s1", colStart: 4, colSpan: 1, rowStart: 1, rowSpan: 1 },
    { id: "s2", colStart: 4, colSpan: 1, rowStart: 2, rowSpan: 1 },
    { id: "s3", colStart: 4, colSpan: 1, rowStart: 3, rowSpan: 1 },
    { id: "s4", colStart: 1, colSpan: 1, rowStart: 4, rowSpan: 1 },
    { id: "s5", colStart: 2, colSpan: 1, rowStart: 4, rowSpan: 1 },
    { id: "s6", colStart: 3, colSpan: 1, rowStart: 4, rowSpan: 1 },
    { id: "s7", colStart: 4, colSpan: 1, rowStart: 4, rowSpan: 1 },
  ],
};

function GridModeSelector({
  mode,
  onChange,
}: {
  mode: GridMode;
  onChange: (mode: GridMode) => void;
}) {
  return (
    <div className="flex gap-1 rounded-md border border-neutral-200 dark:border-[#2A2D3A] bg-white dark:bg-[#1A1D27] p-0.5">
      {GRID_MODES.map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
            mode === m
              ? "bg-brand text-white"
              : "text-neutral-500 dark:text-white/40 hover:text-neutral-600 dark:hover:text-white/70"
          }`}
        >
          {GRID_MODE_LABELS[m]}
        </button>
      ))}
    </div>
  );
}

function LiveStreamPlayer({
  streamUrl,
  streamUrlHigh,
  name,
  compact,
  landscapeOnFullscreen,
}: {
  streamUrl: string;
  streamUrlHigh?: string | null;
  name: string;
  compact?: boolean;
  landscapeOnFullscreen?: boolean;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const isFullscreen = useIsFullscreen(boxRef);
  useLandscapeInFullscreen(isFullscreen, landscapeOnFullscreen);

  const activeUrl = isFullscreen && streamUrlHigh ? streamUrlHigh : streamUrl;
  const { videoRef, status, connect } = useWHEPStream(activeUrl);

  return (
    <div ref={boxRef} className="relative h-full w-full overflow-hidden rounded-lg bg-black">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        controls
        className="h-full w-full object-contain"
      />

      {status === "connecting" && <StreamLoadingOverlay compact={compact} />}
      {status === "failed" && (
        <StreamErrorOverlay compact={compact} onReconnect={() => connect()} />
      )}

      <StreamStatusBadge label="LIVE" name={name} status={status} compact={compact} />
    </div>
  );
}

function EventVideoPlayer({
  event,
  onClose,
  landscapeOnFullscreen,
}: {
  event: StompEventResponse;
  onClose: () => void;
  landscapeOnFullscreen?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  useLandscapeVideo(boxRef, landscapeOnFullscreen);
  const hasVideo = !!event.video?.url;

  useEffect(() => {
    if (hasVideo && videoRef.current) {
      videoRef.current.load();
    }
  }, [event.video?.url, hasVideo]);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-black">
      {hasVideo ? (
        <video
          ref={videoRef}
          src={event.video!.url}
          controls
          autoPlay
          playsInline
          className="h-full w-full object-contain"
        />
      ) : (
        <div className="relative h-full w-full">
          {event.snapshot?.url && (
            <img
              src={event.snapshot.url}
              alt="snapshot"
              className="h-full w-full object-contain opacity-40"
            />
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            <p className="text-sm font-medium text-white">현장 영상 제작 중...</p>
            <p className="mt-1 text-xs text-white/40">완료되면 자동으로 재생됩니다</p>
          </div>
        </div>
      )}

      <div className="absolute left-0 right-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded bg-[#CA0014]/80 px-1.5 py-0.5 font-bold text-white">
            {event.type.replace("_", " ")}
          </span>
          <span className="text-white/60">{event.name}</span>
          <span className="text-white/40">{new Date(event.timestamp).toLocaleString("ko-KR")}</span>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="absolute bottom-2 left-2 flex items-center gap-2 rounded-lg bg-black/60 px-2.5 py-1.5 text-xs font-bold backdrop-blur-sm">
        <span className={hasVideo ? "text-green-400" : "text-yellow-400"}>
          {hasVideo ? "REC" : "PROCESSING"}
        </span>
        <span className="h-3 w-px bg-white/40" />
        <span className="text-white">{event.name}</span>
      </div>
    </div>
  );
}

function EmptySlot() {
  return (
    <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-neutral-200 dark:border-[#2A2D3A] bg-white dark:bg-[#1A1D27]">
      <span className="text-xs text-neutral-400 dark:text-white/20">빈 채널</span>
    </div>
  );
}

function LiveGrid({
  items,
  gridMode,
  page,
}: {
  items: CCTVStreamItem[];
  gridMode: "2x2" | "3x3" | "4x4";
  page: number;
}) {
  const template = GRID_TEMPLATES[gridMode];
  const perPage = template.cells.length;
  const pageItems = items.slice(page * perPage, (page + 1) * perPage);
  const emptyCount = Math.max(0, perPage - pageItems.length);
  const compact = gridMode === "4x4" || gridMode === "3x3";

  return (
    <GridLayout template={template} gap={6} editable className="h-full">
      {pageItems.map((item, i) => (
        <Widget
          key={item.streamName}
          id={`cam-${i}`}
          border={false}
          className="h-full overflow-hidden rounded-lg bg-transparent shadow-none"
          contentClassName="h-full p-0"
        >
          <LiveStreamPlayer
            streamUrl={compact ? (item.whepUrlLow ?? item.whepUrl) : item.whepUrl}
            streamUrlHigh={item.whepUrl}
            name={item.displayName}
            compact={compact}
          />
        </Widget>
      ))}
      {Array.from({ length: emptyCount }, (_, i) => (
        <Widget
          key={`empty-${i}`}
          id={`empty-${i}`}
          border={false}
          className="h-full overflow-hidden rounded-lg bg-transparent shadow-none"
          contentClassName="h-full p-0"
        >
          <EmptySlot />
        </Widget>
      ))}
    </GridLayout>
  );
}

function SpotlightGrid({ items, page }: { items: CCTVStreamItem[]; page: number }) {
  const perPage = SPOTLIGHT_TEMPLATE.cells.length;
  const pageItems = items.slice(page * perPage, (page + 1) * perPage);
  const emptyCount = Math.max(0, perPage - pageItems.length);

  const firstId = pageItems[0]?.streamName ?? "";
  const [spotlightId, setSpotlightId] = useState(firstId);
  const [prevFirstId, setPrevFirstId] = useState(firstId);
  if (firstId !== prevFirstId) {
    setPrevFirstId(firstId);
    setSpotlightId(firstId);
  }

  const handleLayoutChange = (e: LayoutChangeEvent) => {
    if (e.type !== "swap") return;
    const big = e.layout.placements.find((p) => p.cellId === "big");
    if (big) setSpotlightId(big.widgetId);
  };

  return (
    <GridLayout
      template={SPOTLIGHT_TEMPLATE}
      gap={6}
      editable
      onLayoutChange={handleLayoutChange}
      className="h-full"
    >
      {pageItems.map((item) => {
        const isBig = item.streamName === spotlightId;
        return (
          <Widget
            key={item.streamName}
            id={item.streamName}
            border={false}
            className="h-full overflow-hidden rounded-lg bg-transparent shadow-none"
            contentClassName="h-full p-0"
          >
            <LiveStreamPlayer
              streamUrl={isBig ? item.whepUrl : (item.whepUrlLow ?? item.whepUrl)}
              streamUrlHigh={item.whepUrl}
              name={item.displayName}
              compact={!isBig}
            />
          </Widget>
        );
      })}
      {Array.from({ length: emptyCount }, (_, i) => (
        <Widget
          key={`empty-${i}`}
          id={`empty-${i}`}
          border={false}
          className="h-full overflow-hidden rounded-lg bg-transparent shadow-none"
          contentClassName="h-full p-0"
        >
          <EmptySlot />
        </Widget>
      ))}
    </GridLayout>
  );
}

function PlaybackButton({ siteId }: { siteId: string | undefined }) {
  const navigate = useNavigate();
  const enabled = isPlaybackEnabledSite(siteId);

  return (
    <button
      type="button"
      onClick={enabled ? () => navigate(`/cctv-playback/${siteId}`) : undefined}
      disabled={!enabled}
      aria-label="녹화영상 페이지로 이동"
      title={enabled ? "녹화영상 보기" : "이 현장은 녹화영상이 제공되지 않습니다"}
      className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
        enabled
          ? "border-brand/40 bg-brand/10 text-brand hover:bg-brand/20"
          : "cursor-not-allowed border-neutral-200 dark:border-[#2A2D3A] bg-white dark:bg-[#1A1D27] text-neutral-400 dark:text-white/20"
      }`}
    >
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      PLAY BACK
    </button>
  );
}

interface VideoPanelProps {
  siteId?: string;
  items: CCTVStreamItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  selectedStream: string;
  onStreamChange: (streamName: string) => void;
  focusStream?: string | null;
  selectedEvent: StompEventResponse | null;
  onClearEvent: () => void;
  isMobile?: boolean;
}

export function VideoPanel({
  siteId,
  items,
  isLoading,
  isError,
  error,
  selectedStream,
  onStreamChange,
  focusStream,
  selectedEvent,
  onClearEvent,
  isMobile,
}: VideoPanelProps) {
  const [userGridMode, setUserGridMode] = useState<GridMode | null>(null);
  const [page, setPage] = useState(0);

  const autoGridMode: GridMode =
    items.length <= 1 ? "1x1" : items.length <= 4 ? "2x2" : items.length <= 9 ? "3x3" : "4x4";
  const focusMode: GridMode | null =
    focusStream && items.some((i) => i.streamName === focusStream) ? "1x1" : null;
  const gridMode = userGridMode ?? focusMode ?? autoGridMode;

  const [lastSiteId, setLastSiteId] = useState(siteId);
  if (siteId !== lastSiteId) {
    setLastSiteId(siteId);
    setUserGridMode(null);
    setPage(0);
  }

  const effectiveGridMode = isMobile ? "1x1" : gridMode;

  const perPage =
    effectiveGridMode === "1x1"
      ? 1
      : effectiveGridMode === "2x2"
        ? 4
        : effectiveGridMode === "3x3"
          ? 9
          : effectiveGridMode === "spot"
            ? 8
            : 16;
  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const safePage = Math.min(page, totalPages - 1);

  if (selectedEvent) {
    return (
      <div className="flex h-full flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-neutral-600 dark:text-white/60">
            이벤트 영상
          </span>
          <button
            onClick={onClearEvent}
            className="rounded-md border border-neutral-200 dark:border-[#2A2D3A] bg-neutral-100 dark:bg-[#252833] px-3 py-1.5 text-xs text-neutral-600 dark:text-white/60 transition-colors hover:text-neutral-900 dark:hover:text-white"
          >
            라이브로 돌아가기
          </button>
        </div>
        <div className="flex-1">
          <EventVideoPlayer
            event={selectedEvent}
            onClose={onClearEvent}
            landscapeOnFullscreen={isMobile}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 dark:border-white/30 border-t-neutral-900 dark:border-t-white" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[#CA0014]">
        {error?.message || "CCTV 목록 로딩 실패"}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-neutral-500 dark:text-white/40">
        등록된 CCTV가 없습니다
      </div>
    );
  }

  const activeStream = selectedStream || items[0]!.streamName;
  const activeItem = items.find((i) => i.streamName === activeStream) ?? items[0]!;
  const isSingle = effectiveGridMode === "1x1";

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="hidden shrink-0 text-sm font-medium text-neutral-600 dark:text-white/60 sm:inline">
            라이브 스트림
          </span>
          {isSingle && (
            <CCTVSelect items={items} selected={activeStream} onChange={onStreamChange} />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {!isSingle && totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded p-1 text-neutral-500 dark:text-white/50 transition-colors hover:bg-neutral-100 dark:hover:bg-[#252833] hover:text-neutral-900 dark:hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <span className="min-w-[3rem] text-center text-xs text-neutral-500 dark:text-white/50">
                {safePage + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
                disabled={safePage >= totalPages - 1}
                className="rounded p-1 text-neutral-500 dark:text-white/50 transition-colors hover:bg-neutral-100 dark:hover:bg-[#252833] hover:text-neutral-900 dark:hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
          {!isMobile && (
            <GridModeSelector
              mode={gridMode}
              onChange={(m) => {
                setUserGridMode(m);
                setPage(0);
              }}
            />
          )}
          <PlaybackButton siteId={siteId} />
        </div>
      </div>

      <div className="flex-1 min-h-0">
        {isSingle ? (
          <div className="relative h-full w-full">
            <LiveStreamPlayer
              streamUrl={activeItem.whepUrl}
              name={activeItem.displayName}
              landscapeOnFullscreen={isMobile}
            />
            {!isMobile && activeItem.cctv.ptz && (
              <div className="absolute bottom-16 right-3 z-10">
                <PTZControl key={activeItem.cctv.id} cctvId={activeItem.cctv.id} />
              </div>
            )}
          </div>
        ) : effectiveGridMode === "spot" ? (
          <SpotlightGrid items={items} page={safePage} />
        ) : (
          <LiveGrid
            items={items}
            gridMode={effectiveGridMode as "2x2" | "3x3" | "4x4"}
            page={safePage}
          />
        )}
      </div>
    </div>
  );
}
